import type { ItemFieldRow } from './item-field-reader';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { readInventoryField, readLayerListEntryUuid, readItemUuidSlot } from './inventory-raw-readers';
import { createFormulaReadContext } from './formula-read-context';
import { compileInventorySize } from './inventory-size';
import type { InventorySizeCatalog } from './inventory-size';
import { compileInventoryBaseEconomy } from './inventory-base-economy';
import { compileInventoryFinalEconomy } from './inventory-final-economy';
import { createChargeReader, layerEnchantmentOntoInventory } from './enchantment-inventory';
import { createEnchantmentTemplateBuilder } from './enchantment-template';
import { compileInventoryContainer, isInventoryContainer } from './inventory-container';
import { compileInventoryGrossWeight } from './inventory-gross-weight';
import { createInventoryEconomyReader } from './inventory-economy-reader';
import { createInventoryReadContext } from './inventory-read-context';
import { createInventoryInstanceResolver } from './inventory-instance-resolver';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { phpJsonTruthy } from './php-json-values';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import { initializeInventoryCompile } from './inventory-initialization';
import { compileVitality } from './vitality-build';
import { createVitalityReader } from './vitality-reader';
import { createInventoryStatusCompilation } from './inventory-status-compilation';
import type { StatusLayeringMetadata } from './status-layering';
import type { IntegerRollupMetadata } from './integer-layer-rollup';
import { appendEnvironmentalSource, compileInventoryEnvironment, readInventoryFloorEnvironmentContext } from './inventory-environment';
import type { InventoryEnvironmentMetadata } from './inventory-environment';
import { compileInventoryBaseSpeeds } from './inventory-base-speed';
import type { InventoryBaseSpeedMetadata } from './inventory-base-speed';
import { compileInventoryKeyLinks } from './inventory-key-links';
import { readCreatureStatuses } from './creature-status-reader';
import { compileInventoryEncounterLinks, compileEncounterLinkRow } from './inventory-encounter-links';
import { compilePictureRow } from './picture-row-construction';
import { compileInventoryPictures } from './inventory-picture-decorator';
import { readInventoryEffectiveMeshScale, readInventoryTransform } from './inventory-transform';
import { leanStatusEntry } from './status-row-construction';
import { assertUncomputedFromBase, fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FormulaReadDependencies } from './formula-read-context';
import type { InventoryEnvironmentDependencies } from './inventory-environment';
import { constructInventoryDefinitionShells, constructInventoryDefinitionReferences } from './from-base-inventory-references';
import type { InventoryReferenceMetadata } from './from-base-inventory-references';
import { constructInventoryDefinitionChoices } from './from-base-inventory-choices';
import { createFromBaseInventoryOrder } from './from-base-inventory-order';
import { normalizeInventoryDefinition } from './from-base-inventory-normalization';
import { constructInventoryDefinitionAudio, constructInventoryDefinitionVideos } from './from-base-inventory-media';
import type { InventoryMediaMetadata } from './from-base-inventory-media';
import { finalizeInventoryDefinitionLayerSurface } from './from-base-inventory-layer-surface';
import { constructDefinitionItemsFromBase } from '../port/k1-compile/from-base/definition-source';
import { internInventoryDefinition, elideEmptyInventoryDefinitionLayers } from './from-base-inventory-catalog';
import type { InventoryDefinitionCatalogMetadata } from './from-base-inventory-catalog';
import { constructInventoryDefinitionAssets } from './from-base-inventory-assets';
import type { InventoryDefinitionAssetMetadata } from './from-base-inventory-assets';
import { projectDefinitionPendingCacheRead } from './definition-cache-surface';
import { compilePlSignatures, createPlSignatureJsonSources } from './pl-signature';
import type { PlSignatureMetadata } from './pl-signature';
import { fromBaseEnchantmentMembers, applyFromBaseIntrinsicEnchantment } from './from-base-enchantment-rows';
import { readFromBaseDerivedMeshMass } from './from-base-derived-mesh-mass';

type Row = Record<string, unknown>;
export interface FromBaseInventoryMetadata {
    layerCatalog: ProviderCatalogRows & InventoryReferenceMetadata['layerCatalog'] & Readonly<Record<string, { aggregation_strategy?: string }>>;
    shellRegistries?: InventoryReferenceMetadata['shellRegistries'];
    shellRegistriesComplete?: boolean;
    shellRows?: InventoryReferenceMetadata['shellRows'];
    abilityTypes: readonly string[];
    sizeCatalog: InventorySizeCatalog;
    dynamicLayers: readonly string[];
    spellCosts: readonly number[];
    offHandId: string;
    objectTypes: readonly string[];
    statusLayering: StatusLayeringMetadata;
    integerRollup: IntegerRollupMetadata;
    environment: InventoryEnvironmentMetadata;
    baseSpeed: InventoryBaseSpeedMetadata;
    lockedId: string;
    cdnUrl?: string;
    compositions?: readonly Row[];
    definitionMedia?: InventoryMediaMetadata;
    /** Encounter floor construction shares its outer definition query context. */
    environmentDefinition?: (id: string) => Row | null;
    definitionCatalog?: InventoryDefinitionCatalogMetadata;
    definitionAssets?: InventoryDefinitionAssetMetadata;
    /** Raw native layer-catalog iteration order, captured alongside the source snapshot. */
    definitionLayerOrder?: readonly string[];
    plSignature?: PlSignatureMetadata;
    /** Exact enrollment and raw geometry measurements, never precomputed weight. */
    derivedWeightRollout: { item_id: string; inventory_ids: readonly string[] };
}
export interface FromBaseInventoryNode extends Row {
    inventory: Row;
    base_rev: number;
    statuses: { target: readonly Row[]; caster: readonly Row[] };
    charges: readonly Row[];
}
export interface FromBaseInventoryTree {
    pl_signature?: PlSignatureMetadata;
    entity_id: string;
    base_rev: number;
    inventory: readonly FromBaseInventoryNode[];
    enchantment_templates: readonly Row[];
    enchantment_template_enchantments: readonly Row[];
    missing_definition_ids?: readonly string[];
    /** Raw governing source, supplied separately until C1 closes possessor context. */
    possessor?: Row;
    /** Complete SELECT-only links for the inventory ids; absence is unknown. */
    encounter_links?: readonly Row[];
    /** Complete reads for saved presentation pointers, including explicit misses. */
    picture_sources?: readonly Row[];
    picture_lookup_ids?: readonly string[];
    /** Native SELECT order over the complete saved tree, with raw identity/name witnesses. */
    inventory_order?: readonly Row[];
    /** Own-definition video SELECT closure; explicit misses are required. */
    definition_videos?: { rows: readonly Row[]; lookupIds: readonly string[] };
    /** Immutable frontend registries; C1's rows alone cannot supply rule metadata. */
    metadata?: FromBaseInventoryMetadata;
    /** Batched SELECT/registry inputs delivered with the saved tree. */
    inventory_metadata?: Pick<FromBaseInventoryMetadata, 'shellRows' | 'shellRegistries' | 'shellRegistriesComplete'
        | 'definitionCatalog'> & { definitionAssets?: Partial<InventoryDefinitionAssetMetadata>; definitionMedia?: Partial<InventoryMediaMetadata> };
}
export const FROM_BASE_INVENTORY_REMAINING_STAGES = [
    'definition-finalization', 'definition-formulas',
    'possessor-equipped-counts', 'catalog-lean-normalization',
    'database-collation-order',
    'definition-layer-order',
] as const;
export const FROM_BASE_INVENTORY_IMPLEMENTED_STAGES = [
    'saved-tree-identity-position', 'definition-enchantment-economy', 'size-capacity', 'base-economy', 'enchantment-economy-provenance',
    'charges-slot-final-economy', 'container-totals', 'gross-weight',
    'definition-classification-vitality', 'object-vitality', 'status-light-magic-signature',
    'environment-context', 'key-links', 'base-speed', 'encounter-links',
    'definition-enchantment-source-closure',
    'pictures', 'derived-mesh-weight', 'closure-state', 'catalog-lean-status-records', 'definition-numeric-economy', 'definition-source-finalization',
    'inventory-source-lean-normalization',
    'definition-lock-classification',
    'definition-composition-source-surface',
    'definition-shell-references',
    'definition-ability-choice-options',
    'definition-nest-dedup-pl-surface',
    'definition-full-nested-lock-surface',
    'definition-empty-layer-vitality-mirror',
] as const;
const FROM_BASE_INVENTORY_FINALIZED_STAGES = ['definition-finalization', 'definition-formulas',
    'catalog-lean-normalization'] as const;
export interface FromBaseInventoryResult {
    kind: 'missing-stage';
    /** Constructed tree; missingStages discloses any unavailable source/projection. */
    inventory?: Row[];
    /** One-row closure identities; legacy encounter travel links never merge them. */
    closureStates?: Readonly<Record<string, { phase: string; revision: number }>>;
    derivedWeights?: Readonly<Record<string, Row>>;
    /** Prefix or finalized definitions, according to the supplied raw metadata. */
    definitions?: Readonly<Record<string, Row>>;
    completedStages: readonly string[];
    missingStages: readonly string[];
    detail?: string;
}
const upper = (value: string): string => value.toUpperCase();
function id(value: unknown, label: string): string {
    if (!fromBaseUuid(value)) throw new TypeError(`Invalid base inventory ${label}: ${String(value)}`);
    return upper(value);
}
function array(value: unknown, label: string): asserts value is readonly Row[] {
    if (!Array.isArray(value) || !value.every(fromBaseRecord)) throw new TypeError(`Invalid base inventory ${label}`);
}

/** Shared with the captured weather-host replay. Inventory-only hosts do not
 * normally enter the immunity gate, but future hosts must receive native readers. */
export function createFromBaseInventoryEnvironmentDependencies(metadata: InventoryEnvironmentMetadata,
    common: FormulaReadDependencies): InventoryEnvironmentDependencies {
    const readers = createFormulaReadContext(common);
    const providers = createCatalogDefaultProviderReader({ catalogRows: common.catalogRows, nodeItemId: readNodeItemId,
        resolveCarrierRef: common.resolveCarrierRef });
    return { metadata, queryDefinition: common.resolveCarrierRef,
        immunityReaders: { entries: (row, name) => Object.values(readers.layerEntries(row, name)),
            value: readers.layerValue, providers: row => [...providers.providers(row)], definition: common.resolveCarrierRef,
            conditionsMet: (): never => { throw new FromBaseStageMissing('environment-immunity-context', 'passive-conditions'); },
            isAura: row => ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target']
                .every(name => readers.layerValue(row, name) !== null) } };
}

/** Construct the saved tree from raw rows in native build order. Unknown inputs
 * disclose their stage; neither prior compiled blobs nor viewer permissions enter it. */
export function compileInventoryTreeFromBase(baseTree: FromBaseInventoryTree,
    catalog: Readonly<Record<string, Row>> | readonly [], definitionIds?: readonly string[]): FromBaseInventoryResult {
    if (definitionIds) { baseTree = fromBaseClone(baseTree); catalog = fromBaseClone(catalog); }
    assertUncomputedFromBase(baseTree);
    assertUncomputedFromBase(catalog, 'catalog');
    const owner = id(baseTree.entity_id, 'entity_id');
    if (!Number.isSafeInteger(baseTree.base_rev) || baseTree.base_rev < 0 || !Array.isArray(baseTree.inventory)
        || !(fromBaseRecord(catalog) || (Array.isArray(catalog) && catalog.length === 0))) throw new TypeError('Invalid base inventory envelope');
    // A complete SELECT with no saved members is a known empty surface. Definition,
    // ordering and possessor stages have no work and cannot make this floor unknown.
    if (baseTree.inventory.length === 0 && !definitionIds) return { kind: 'missing-stage', inventory: [],
        completedStages: ['saved-tree-identity-position'], missingStages: [] };
    const metadata = baseTree.metadata && mergeInventoryMetadata(baseTree.metadata, baseTree.inventory_metadata);
    if (metadata) metadata.plSignature = baseTree.pl_signature ?? metadata.plSignature;
    if (!metadata) return { kind: 'missing-stage', completedStages: [], missingStages: ['frontend-metadata', ...FROM_BASE_INVENTORY_REMAINING_STAGES] };
    array(baseTree.enchantment_templates, 'enchantment_templates');
    array(baseTree.enchantment_template_enchantments, 'enchantment_template_enchantments');
    const signatureJson = createPlSignatureJsonSources(catalog, metadata.definitionAssets?.vfxRows ?? [], metadata.shellRegistries);
    const compositionMultipliers = new Map<string, unknown>();
    const definitions = new Map<string, Row>();
    for (const [key, source] of Object.entries(catalog)) {
        if (!fromBaseRecord(source) || !fromBaseRecord(source.items)
            || !(fromBaseRecord(source.item_layer_types) || (Array.isArray(source.item_layer_types) && source.item_layer_types.length === 0))
            || id(source.items.item_id, 'definition identity') !== id(key, 'catalog key')) throw new TypeError(`Invalid authored inventory definition: ${key}`);
        const row = fromBaseClone(source);
        // Canonical authored buckets win. C1's legacy mirrors are not independent layers.
        for (const name of Object.keys(row.item_layer_types as Row)) delete row[name];
        if (definitions.has(upper(key))) throw new TypeError(`Duplicate authored inventory definition: ${key}`);
        definitions.set(upper(key), row);
    }
    const missing = new Set((baseTree.missing_definition_ids ?? []).map(value => {
        const key = id(value, 'known definition miss');
        if (definitions.has(key)) throw new TypeError(`Invalid known definition miss: ${value}`);
        return key;
    }));
    const nodes = new Map<string, FromBaseInventoryNode>(), children = new Map<string, string[]>();
    for (const source of baseTree.inventory) {
        if (!fromBaseRecord(source.inventory) || !Number.isSafeInteger(source.base_rev) || source.base_rev < 0)
            throw new TypeError('Invalid saved inventory node');
        const key = id(readInventoryField(source, 'inventory_id'), 'inventory_id');
        if (key === owner || nodes.has(key)) throw new TypeError(`Duplicate base inventory: ${key}`);
        id(readInventoryField(source, 'inventory_item_id'), 'inventory_item_id');
        const parent = id(readInventoryField(source, 'possess_id'), 'possess_id');
        array(source.charges, 'charges');
        if (!fromBaseRecord(source.statuses)) throw new TypeError('Missing saved inventory statuses');
        array(source.statuses.target, 'target statuses'); array(source.statuses.caster, 'caster statuses');
        nodes.set(key, source);
        const siblings = children.get(parent) ?? []; siblings.push(key); children.set(parent, siblings);
    }
    // Validate the complete saved tree before doing any calculation. Disconnected
    // cycles must not quietly disappear from the root projection.
    const reached = new Set<string>();
    function visit(parent: string, depth: number): void {
        if (depth > 64) throw new TypeError('Base inventory nesting exceeds 64');
        for (const child of children.get(parent) ?? []) {
            if (reached.has(child)) throw new TypeError(`Base inventory cycle: ${child}`);
            reached.add(child); visit(child, depth + 1);
        }
    }
    visit(owner, 0);
    if (reached.size !== nodes.size) throw new TypeError('Base inventory has orphaned rows or a possess cycle');
    const built = new Map<string, Row>(), prepared = new Map<string, Row>(), preparing = new Set<string>();
    const rawDefinition = (value: string): Row | null => {
        const key = id(value, 'reference'), row = definitions.get(key);
        if (!row) {
            if (missing.has(key)) return null;
            throw new FromBaseInputMissing('definition', value);
        }
        return row;
    };
    const definition = (value: string): Row | null => {
        const row = rawDefinition(value), key = upper(value);
        if (!row) return null;
        const cached = prepared.get(key);
        if (cached) return fromBaseClone(cached);
        if (preparing.has(key)) return fromBaseClone(row);
        preparing.add(key);
        try {
            const leadingLayers: string[] = [];
            for (const name of ['cost_gp', 'weight', 'carrying_capacity_pounds', 'cost_gp_multiplier', 'weight_multiplier', 'item_charges']) {
                const value = authoredReaders.layerValue(row, name);
                if (!metadata.definitionCatalog && value !== null && value !== '' && !isPhpNumeric(value))
                    throw new FromBaseStageMissing('definition-formulas', `${key}:${name}`);
            }
            const applied = readers.layerValue(row, 'enchantment_template_applied');
            let result = fromBaseClone(row);
            // Native classification embeds full-nested locks/materials before
            // vitality and intrinsic enchantments.
            if (metadata.objectTypes.includes(String(readers.itemField(result, 'item_type')))
                || readers.itemField(result, 'item_type') === 'map_marker') {
                for (const name of ['lock_type', 'item_composition']) {
                    const entries = Object.entries(authoredReaders.layerEntries(result, name));
                    const fullNest = metadata.layerCatalog[name]?.full_nest;
                    if (fullNest && entries.length === 0) {
                        const layers = { ...result.item_layer_types as Row }; delete layers[name];
                        result.item_layer_types = layers;
                        continue;
                    }
                    const nested: Row = {};
                    for (const [slot, entry] of entries) {
                        const sourceId = readLayerListEntryUuid(entry);
                        if (!fromBaseUuid(sourceId)) continue;
                        const source = definitions.get(upper(sourceId));
                        if (!source) {
                            if (missing.has(upper(sourceId))) continue;
                            throw new FromBaseInputMissing('definition', sourceId);
                        }
                        if (fullNest) {
                            if (preparing.has(upper(sourceId)))
                                throw new FromBaseStageMissing('definition-finalization', `${name === 'item_composition' ? 'composition' : name}-cycle:${sourceId}`);
                            const child = metadata.plSignature ? finalizedDefinition(sourceId) : definition(sourceId);
                            nested[slot] = child && name === 'lock_type'
                                ? constructInventoryDefinitionShells(child, metadata, rawDefinition) : child;
                        } else {
                            const layers = fromBaseClone(source.item_layer_types as Row);
                            if (authoredReaders.layerValue(result, 'damage_type') !== null) delete layers.damage_type;
                            result = appendEnvironmentalSource(result, { item_layer_types: layers }) as Row;
                        }
                    }
                    if (Object.keys(nested).length) result.item_layer_types = { ...result.item_layer_types as Row,
                        [name]: metadata.layerCatalog[name]?.is_multiple
                            ? Object.keys(nested).every((key, index) => key === String(index)) ? Object.values(nested) : nested
                            : Object.values(nested)[0] };
                }
                // Classification defaults/vitality precede intrinsic enchantments.
                const sourceSize = readers.layerValue(result, 'size');
                if (!phpJsonTruthy(sourceSize)) leadingLayers.push('size');
                const size = sourceSize
                    || metadata.sizeCatalog.find(entry => entry.variable_name === 'tiny')?.item_id;
                if (!fromBaseUuid(size)) throw new FromBaseStageMissing('frontend-metadata', 'object-size');
                result.item_layer_types = { ...result.item_layer_types as Row, size };
                const composition = readers.layerValue(result, 'item_composition');
                if (phpJsonTruthy(composition) && !phpJsonTruthy(readers.layerValue(result, 'v_max_override'))) {
                    const scale = metadata.sizeCatalog.find(entry => entry.item_id === size)?.vitality_scale;
                    if (!isPhpNumeric(scale)) throw new FromBaseStageMissing('frontend-metadata', `vitality-scale:${size}`);
                    const compositionId = readLayerListEntryUuid(composition);
                    if (fromBaseUuid(compositionId) && !compositionMultipliers.has(compositionId)) {
                        const material = metadata.plSignature ? finalizedDefinition(compositionId) : definition(compositionId);
                        compositionMultipliers.set(compositionId, readers.layerValue(material, 'object_vitality_multiplier'));
                    }
                    const multiplier = compositionMultipliers.get(compositionId);
                    const authored = readers.layerValue(result, 'v_max');
                    const maximum = Math.max(1, roundPhpDecimal(isPhpNumeric(authored) && Number(authored) > 0 ? Number(authored)
                        : 10 * Number(scale) * (isPhpNumeric(multiplier) && Number(multiplier) > 0 ? Number(multiplier) : 1), 0));
                    result.computed = { vitality: { v_max: maximum, v_current: maximum } };
                }
            }
            if (phpJsonTruthy(applied)) {
                if (!baseTree.enchantment_templates.some(entry => upper(String(entry.enchantment_template_id)) === upper(String(applied))))
                    throw new FromBaseStageMissing('definition-enchantment-source-rows', String(applied));
                // Applied enchantments precede provider-reference expansion on
                // definition shells. The existing flat twin must read the host
                // authored layers here, rather than already expanded providers.
                result = applyFromBaseIntrinsicEnchantment(result, applied, { ...enchantmentReaders,
                    layerValue: authoredReaders.layerValue, layerEntries: authoredReaders.layerEntries,
                    hasLayer: (target, name) => target !== null && (authoredReaders.layerValue(target, name) !== null
                        || phpJsonTruthy(authoredReaders.layerEntries(target, name))),
                }, metadata.dynamicLayers) as Row;
                for (const name of ['weight', 'item_charges', 'current_item_charges', 'item_rechargeable']) {
                    if (Object.prototype.hasOwnProperty.call(result.item_layer_types, name)
                        && (name !== 'weight' || phpJsonFloat(readers.layerValue(result, name) ?? 0) !== 0)) leadingLayers.push(name);
                }
            }
            // The raw catalog is an authoring SELECT; audit columns, blank flags,
            // N/A source and raw media are not the display definition surface.
            result.items = constructDefinitionItemsFromBase(result).items;
            const gallery = pictures({ attached_id: key, attached_item_type: 'item', picture_deleted: 0 });
            if (gallery.length) result.pictures = { item: gallery }; else delete result.pictures;
            if (metadata.definitionAssets)
                result = constructInventoryDefinitionAssets(result, rawDefinition, metadata.definitionAssets,
                    metadata.sizeCatalog.map(row => String(readers.itemField(row, 'item_id'))), readers.layerValue);
            delete result.meshes;
            result = constructInventoryDefinitionChoices(result, readers.layerEntries, rawDefinition);
            if (metadata.definitionMedia)
                result = constructInventoryDefinitionAudio(result, metadata.definitionMedia,
                    metadata.definitionCatalog ? () => null : readers.layerValue,
                    metadata.definitionCatalog ? () => false : hasLayer);
            if (baseTree.definition_videos) {
                if (typeof metadata.cdnUrl !== 'string') throw new FromBaseStageMissing('definition-videos', 'cdn-url');
                result = constructInventoryDefinitionVideos(result, { ...baseTree.definition_videos, cdnUrl: metadata.cdnUrl });
            }
            result = finalizeInventoryDefinitionLayerSurface(result, metadata.definitionLayerOrder, leadingLayers, metadata.layerCatalog);
            prepared.set(key, result); return fromBaseClone(result);
        } finally { preparing.delete(key); }
    };
    const finalized = new Map<string, Row>();
    function finalizedDefinition(value: string, ancestors: readonly string[] = []): Row | null {
        if (!metadata!.definitionAssets) throw new FromBaseStageMissing('definition-mesh-input', 'metadata');
        if (!metadata!.definitionMedia) throw new FromBaseStageMissing('definition-audio-taxonomy', 'metadata');
        if (!metadata!.plSignature) throw new FromBaseStageMissing('definition-pl-signature-input', 'codec-contract-epoch');
        if (readers.itemField(rawDefinition(value), 'item_type') === 'creature')
            throw new FromBaseStageMissing('definition-finalization', `creature-finalizer:${value}`);
        const key = upper(value);
        if (!ancestors.length && finalized.has(key)) {
            // A standalone query's first cache read refreshes audio from the
            // finalized layers; nested cold compiles keep build_item_arr's refs.
            const cached = constructInventoryDefinitionAudio(projectDefinitionPendingCacheRead(finalized.get(key)!),
                metadata!.definitionMedia!, readers.layerValue, hasLayer);
            finalized.set(key, cached);
            return fromBaseClone(cached);
        }
        const source = definition(value);
        if (!source) return null;
        let result = constructInventoryDefinitionReferences(source, metadata!, rawDefinition,
            { fullDefinition: finalizedDefinition, ancestors });
        if (fromBaseRecord(result.item_layer_types) && !Object.keys(result.item_layer_types).length) delete result.item_layer_types;
        // PL belongs to the browser pricing lane. A raw compile has no carried price.
        const computed = fromBaseRecord(result.computed) ? result.computed : {};
        // Carrier composition becomes readable after expansion; native restores
        // that vitality after the PL window rather than during classification.
        const inheritedVitality = authoredReaders.layerValue(rawDefinition(value), 'item_composition') === null
            && fromBaseRecord(computed.vitality);
        const earlyComputed = { ...computed };
        if (inheritedVitality) delete earlyComputed.vitality;
        result = Object.fromEntries([...new Set(['items', 'pictures', 'assets', 'computed', 'item_videos', 'ability_choice_options', 'item_layer_types',
            ...Object.keys(result)])].filter(name => Object.prototype.hasOwnProperty.call(result, name)).map(name => [name, result[name]]));
        if (metadata!.plSignature) earlyComputed._cache = { ...earlyComputed._cache as Row,
            ...compilePlSignatures({ ...result, computed: earlyComputed }, metadata!.plSignature, signatureJson) };
        result.computed = { ...earlyComputed, pl: null, pl_pending: 1,
            ...(inheritedVitality ? { vitality: computed.vitality } : {}) };
        result = normalizeInventoryDefinition(result, metadata!.layerCatalog);
        if (fromBaseRecord(result.item_layer_types) && !Object.keys(result.item_layer_types).length) delete result.item_layer_types;
        // Request-local sentinel holds the uninterned full definition during
        // charged/passive closure reads back to this exact root.
        if (!ancestors.length) finalized.set(key, fromBaseClone(result));
        if (!ancestors.length && metadata!.definitionCatalog)
            result = internInventoryDefinition(result, { ...metadata!.definitionCatalog, identityRows: metadata!.shellRows }, finalizedDefinition);
        result = Object.fromEntries([...new Set(['items', 'pictures', 'assets', 'computed', 'item_videos', 'ability_choice_options', 'item_layer_types',
            ...Object.keys(result).filter(name => name !== 'catalog'), 'catalog'])]
            .filter(name => Object.prototype.hasOwnProperty.call(result, name)).map(name => [name, result[name]]));
        if (!ancestors.length) finalized.set(key, result);
        return result;
    }
    const resolveInventory = (value: string): ItemFieldRow | null => {
        const key = id(value, 'instance reference');
        if (!nodes.has(key)) throw new FromBaseStageMissing('external-inventory-context', value);
        return instance(value);
    };
    const instance = createInventoryInstanceResolver(value => compileNode(id(value, 'instance reference'), 0));
    const inventoryContext = createInventoryReadContext(resolveInventory);
    const inventoryIdentity = (value: string): { item_id: string; item_type: unknown } => {
        const node = nodes.get(id(value, 'instance identity'));
        if (!node) throw new FromBaseStageMissing('external-inventory-context', value);
        const template = String(readInventoryField(node, 'inventory_item_id'));
        return { item_id: template, item_type: readers.itemField(definition(template), 'item_type') };
    };
    const readers = createFormulaReadContext({ inventoryContext, catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: resolveInventory,
        resolveItemName: value => readers.itemField(definition(value), 'item_name'),
        resolveInventoryIdentity: inventoryIdentity,
    });
    const hasLayer = (row: ItemFieldRow, name: string): boolean => readers.layerValue(row, name) !== null || phpJsonTruthy(readers.layerEntries(row, name));
    const statusRows = (row: ItemFieldRow, mode: string = 'target'): ItemFieldRow => readCreatureStatuses(row, mode, definition);
    const common = { inventoryContext, catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: (name: string) => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: resolveInventory,
        resolveInventoryIdentity: inventoryIdentity, resolveItemName: (value: string) => readers.itemField(definition(value), 'item_name') };
    const missingStatusContext = (dependency: string): never => { throw new FromBaseStageMissing('status-context', dependency); };
    const statuses = createInventoryStatusCompilation({ inventoryContext,
        statuses: { ...common, metadata: metadata.statusLayering,
            referenceInputs: { fullItem: value => metadata.definitionCatalog ? finalizedDefinition(value) : definition(value), itemRow: value => definition(value),
                count: (table, _column, value) => table === 'items' ? (definition(value) ? 1 : 0)
                    : nodes.has(id(value, 'status inventory')) ? 1 : missingStatusContext(`inventory:${value}`),
                determineType: value => {
                    if (nodes.has(id(value, 'status origin'))) return 'inventory';
                    definition(value); return 'item';
                },
                dynamicItem: value => resolveInventory(value)! },
            fetchStatuses: () => missingStatusContext('saved-status-rows'), itemName: common.resolveItemName,
            campaignSeconds: () => missingStatusContext('campaign-calendar'), casterTeam: value => missingStatusContext(`caster-team:${value}`) },
        rollup: { ...common, metadata: metadata.integerRollup, queryFullItem: definition },
        // The signature helper also probes malformed authored spell cells;
        // native full-item lookup returns null for a non-UUID, not an unknown id.
        magic: { ...common, fullItem: value => fromBaseUuid(value) ? definition(value) : null } });
    const vitality = createVitalityReader({ inventoryContext, resolveInventoryInstance: resolveInventory });
    const environmentDependencies = createFromBaseInventoryEnvironmentDependencies(metadata.environment,
        { ...common, resolveCarrierRef: metadata.environmentDefinition ?? definition });
    const authoredReaders = createFormulaReadContext({ inventoryContext, catalogRows: {}, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: resolveInventory });
    const field = (row: ItemFieldRow, name: string): unknown => authoredReaders.layerValue(row, name);
    const pictureRows = [...baseTree.inventory.flatMap(node => Array.isArray(node.pictures) ? node.pictures as Row[] : []),
        ...Array.from(definitions.values()).flatMap(row => Array.isArray(row.pictures) ? row.pictures as Row[] : []),
        ...(baseTree.picture_sources ?? [])];
    const pictures = (filters: Readonly<Record<string, unknown>>): Row[] => {
        const aliases: Readonly<Record<string, string>> = { attached_id: 'picture_attached_id', attached_item_type: 'picture_attached_item_type' };
        const matches = pictureRows.filter(row => Object.entries(filters).every(([name, value]) =>
            String(field(row, aliases[name] ?? name) ?? '').toUpperCase() === String(value).toUpperCase()));
        const unique = [...new Map(matches.map(row => [field(row, 'picture_id'), row])).values()];
        unique.sort((a, b) => {
            for (const [index, name] of ['primary_picture', 'picture_thumbnail', 'picture_number', 'picture_i'].entries()) {
                const left = field(a, name), right = field(b, name);
                const delta = left == null || right == null ? Number(left != null) - Number(right != null) : Number(left) - Number(right);
                if (delta) return index < 2 ? -delta : delta;
            }
            return 0;
        });
        if (unique.length && typeof metadata.cdnUrl !== 'string') throw new FromBaseStageMissing('pictures', 'cdn-url');
        return unique.map(row => compilePictureRow(row, { cdnUrl: metadata.cdnUrl! }) as Row);
    };
    const derivedWeights: Record<string, Row> = {};
    const derivedWeight = (target: ItemFieldRow, sizeScale: number): Row & { unit_lb: number } | null => {
        const rollout = metadata.derivedWeightRollout;
        if (upper(String(readInventoryField(target, 'inventory_item_id'))) !== upper(rollout.item_id)
            || !rollout.inventory_ids.some(value => upper(value) === upper(String(readInventoryField(target, 'inventory_id'))))) return null;
        const composition = readers.layerValue(target, 'item_composition');
        if (!metadata.compositions) throw new FromBaseStageMissing('derived-mesh-weight', 'composition-density');
        const density = metadata.compositions.find(row => upper(String(field(row, 'item_id'))) === upper(String(composition)));
        const pounds = density ? field(density, 'density_lb_ft3') : null;
        if (!isPhpNumeric(pounds) || Number(pounds) <= 0 || !Number.isFinite(sizeScale) || sizeScale <= 0) return null;
        const source = definitions.get(upper(rollout.item_id));
        if (!source || !Array.isArray(source.meshes)) throw new FromBaseStageMissing('derived-mesh-weight', 'world-mesh-source');
        const rows = (source.meshes as Row[]).filter(row => Number(field(row, 'mesh_deleted') ?? 0) === 0
            && Number(field(row, 'mesh_unapproved') ?? 0) === 0 && field(row, 'mesh_role') === 'world');
        const rawSelection = readInventoryField(target, 'mesh_id');
        const selection = typeof rawSelection === 'string' ? rawSelection.trim() : null;
        const selected = rows.find(row => selection && upper(String(field(row, 'mesh_id'))) === upper(selection)
            && field(row, 'mesh_body_profile') === 'any');
        const variant = selected ? field(selected, 'mesh_variant_key') : null;
        const tiers: Readonly<Record<string, number>> = { high: 0, medium: 1, low: 2 };
        const eligible = rows.filter(row => field(row, 'mesh_state_key') === 'default' && field(row, 'mesh_body_profile') === 'any'
            && (variant === null || field(row, 'mesh_variant_key') === variant));
        eligible.sort((a, b) => (tiers[String(field(a, 'mesh_quality_tier'))] ?? 9) - (tiers[String(field(b, 'mesh_quality_tier'))] ?? 9)
            || Number(field(b, 'primary_mesh') ?? 0) - Number(field(a, 'primary_mesh') ?? 0)
            || Number(field(a, 'mesh_number') ?? 1) - Number(field(b, 'mesh_number') ?? 1)
            || upper(String(field(a, 'mesh_id'))).localeCompare(upper(String(field(b, 'mesh_id')))));
        const mesh = eligible[0];
        if (!mesh || phpJsonTruthy(field(mesh, 'mesh_part_key'))) return null;
        const mass = readFromBaseDerivedMeshMass(field(mesh, 'mesh_generation_profile'));
        if (!mass) return null;
        const { volume, measurement } = mass;
        // The shared transform reader owns the same normalization used by cost and rendering.
        const scale = readInventoryEffectiveMeshScale(target, sizeScale);
        const axes = readInventoryTransform(target)?.scaleXYZ;
        const axisProduct = Array.isArray(axes) ? axes.reduce((product: number, axis: unknown) => product * phpJsonFloat(axis), 1) : 1;
        const result = { unit_lb: roundPhpDecimal(volume * 35.3147 * Number(pounds) * scale ** 3 * axisProduct, 2), effective_scale: scale,
            mesh_id: field(mesh, 'mesh_id'), sha256: field(mesh, 'mesh_sha256'), solid_volume_m3: volume,
            density_lb_ft3: Number(pounds), size_scale: sizeScale, measurement_status: measurement };
        derivedWeights[id(readInventoryField(target, 'inventory_id'), 'derived weight')] = result;
        return result;
    };
    const closureStates: Record<string, { phase: string; revision: number }> = {};
    const economy = createInventoryEconomyReader({ inventoryContext, resolveInventoryInstance: resolveInventory });
    const charges = createChargeReader(null, (holder, item) => {
        const node = nodes.get(id(holder, 'charge holder'));
        if (!node) throw new FromBaseStageMissing('charges', String(holder));
        const rows = node.charges.filter(row => upper(String(row.charge_holder)) === upper(String(holder))
            && upper(String(row.item_id)) === upper(String(item)) && Number(row.charge_deleted ?? 0) === 0);
        if (rows.length > 1) throw new TypeError('Duplicate saved charge pool');
        return rows.length ? rows[0]!.charges : false;
    });
    const enchantment = createEnchantmentTemplateBuilder({
        ...readers, hasLayer: (row, name) => row !== null && hasLayer(row, name),
        dynamicLayers: metadata.dynamicLayers, spellCosts: metadata.spellCosts,
        templateRows: value => baseTree.enchantment_templates.filter(row => upper(String(row.enchantment_template_id)) === upper(value)),
        memberRows: value => fromBaseEnchantmentMembers(baseTree.enchantment_template_enchantments, value, rawDefinition),
        fullItem: value => metadata.definitionCatalog && !preparing.has(upper(value)) ? finalizedDefinition(value) : definition(value),
    }, true);
    const enchantmentReaders = { ...readers, hasLayer: (target: ItemFieldRow | null, name: string) => target !== null && hasLayer(target, name),
        buildTemplate: enchantment, charges };
    const inventoryName = (key: string): string => {
        const node = nodes.get(key)!;
        const saved = readInventoryField(node, 'inventory_name');
        const name = String(saved !== null && saved !== '' ? saved
            : readers.itemField(definition(String(readInventoryField(node, 'inventory_item_id'))), 'item_name') || 'Unknown');
        if (/[^\x00-\x7f]/.test(name)) throw new FromBaseStageMissing('database-collation-order', key);
        return name;
    };
    let nativeOrder: ((parent: string) => string[]) | undefined;
    const orderedChildren = (parent: string): string[] => {
        if (baseTree.inventory_order) {
            nativeOrder ??= createFromBaseInventoryOrder(nodes, baseTree.inventory_order, rawDefinition);
            return nativeOrder(parent);
        }
        return (children.get(parent) ?? []).map(key => ({ key, name: inventoryName(key) }))
            // Unheld SELECT order remains a named gap. Preserve the earlier
            // ASCII checkpoint while the loader's order supplement is absent.
            .sort((a, b) => {
                const x = a.name.toLowerCase(), y = b.name.toLowerCase();
                return x < y ? -1 : x > y ? 1 : a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
            }).map(entry => entry.key);
    };
    function prefetchTemplatePictures(parent: string): void {
        if (!metadata!.definitionCatalog) return;
        const templates = new Set<string>();
        for (const key of orderedChildren(parent)) {
            const node = nodes.get(key)!;
            if (readItemUuidSlot(readInventoryField(node, 'inventory_presentation_pic_id'))
                || readItemUuidSlot(readInventoryField(node, 'inventory_thumbnail_pic_id'))
                || pictures({ attached_id: key, attached_item_type: 'inventory_picture', picture_deleted: 0 }).length) continue;
            templates.add(String(readInventoryField(node, 'inventory_item_id')));
        }
        // fetch_inventories queries each eligible template while gathering
        // picture pointers, before constructing any sibling instance.
        for (const template of templates) finalizedDefinition(template);
    }
    function compileNode(key: string, depth: number): Row {
        return inventoryContext.withoutInstanceBridge(nodes.get(key)!, () => constructNode(key, depth));
    }
    function compileChildren(parent: string, depth: number, rows: Row[] = []): Row[] {
        for (const key of orderedChildren(parent)) {
            const row = compileNode(key, depth);
            rows.push(row);
            // A non-enclosing holder is a placement anchor. Its resting items
            // remain peers, each with its own economy and saved world transform.
            if (!isInventoryContainer(row, { itemField: readers.itemField, hasLayer })) compileChildren(key, depth + 1, rows);
        }
        return rows;
    }
    function constructNode(key: string, depth: number): Row {
        const cached = built.get(key); if (cached) return fromBaseClone(cached);
        if (depth > 64) throw new FromBaseStageMissing('inventory-context-cycle', key);
        const node = nodes.get(key)!, templateId = String(readInventoryField(node, 'inventory_item_id'));
        const template = metadata!.definitionCatalog ? finalizedDefinition(templateId) : definition(templateId);
        if (!template) throw new FromBaseStageMissing('definition-input', `known-missing-definition:${templateId}`);
        const defaultSize = readers.layerValue(template, 'size')
            ?? metadata!.sizeCatalog.find(entry => entry.variable_name === 'tiny')?.item_id;
        if (!fromBaseUuid(defaultSize)) throw new FromBaseStageMissing('frontend-metadata', 'template-size');
        const parent = id(readInventoryField(node, 'possess_id'), 'possess_id');
        const possessor = baseTree.possessor;
        if (parent === owner && !possessor) throw new FromBaseStageMissing('possessor-context', owner);
        const type = parent !== owner ? 'inventory' : fromBaseRecord(possessor?.player_characters) ? 'player_character'
            : fromBaseRecord(possessor?.encounters) ? 'encounter' : fromBaseRecord(possessor?.encounter_creatures) ? 'encounter_creature'
            : fromBaseRecord(possessor?.inventory) ? 'inventory' : fromBaseRecord(possessor?.locations) ? 'location' : null;
        if (!type) throw new FromBaseStageMissing('possessor-context', owner);
        const saved = Object.fromEntries(Object.keys(node.inventory).map(name => [name, readInventoryField(node, name)]));
        // Supply the SELECT join through the native readers; blank stored prose
        // remains an override, while null inherits the authored template.
        for (const name of ['inventory_written_in_language', 'inventory_gm_notes', 'inventory_cost_gp', 'equipped',
            'enchantment_template_id', 'inventory_inspection_dc']) saved[name] = readInventoryField(node, name);
        saved.item_qty = readInventoryField(node, 'item_qty');
        const name = readInventoryField(node, 'inventory_name');
        saved.true_inventory_name = name !== null && name !== '' ? name : readers.itemField(template, 'item_name') || 'Unknown';
        for (const field of ['item_type', 'item_description', 'flavor_text']) saved[field] = readers.itemField(template, field);
        let row = initializeInventoryCompile(saved, { possessor_type: type }, {
            resolveTemplate: value => upper(value) === upper(templateId) ? template
                : metadata!.definitionCatalog ? finalizedDefinition(value) : definition(value),
            resolveItemType: () => readers.itemField(template, 'item_type'),
            determinePossessorType: () => type, tinySizeId: defaultSize, hasLayer,
            readVitality: vitality, compileVitality: (target, source) => compileVitality(target, source, {
                readVitality: vitality, readLayerValue: readers.layerValue, readItemField: readers.itemField,
                readStatuses: statusRows, readBlightedStack: () => missingStatusContext('living-inventory-vitality'),
                buildStatusShell: () => null,
            }),
        }) as Row;
        // Keep an explicit zero cost override through the economy stages. The
        // existing checkpoint preserves it; native initialization elides it early.
        const costOverride = readInventoryField(node, 'inventory_cost_gp');
        if (costOverride === 0 || costOverride === '0') (row.inventory as Row).inventory_cost_gp = costOverride;
        row.item_layer_types = { ...row.item_layer_types as Row, size: defaultSize };
        const sized = compileInventorySize(row, metadata!.sizeCatalog, readers.layerValue);
        row = compileInventoryBaseEconomy(sized.row, sized.sizeScale, { ...readers, derivedWeight }) as Row;
        const enchantmentId = readInventoryField(node, 'enchantment_template_id');
        if (phpJsonTruthy(enchantmentId) && !baseTree.enchantment_templates.some(entry => upper(String(entry.enchantment_template_id)) === upper(String(enchantmentId))))
            throw new FromBaseStageMissing('enchantment-source-rows', String(enchantmentId));
        row = layerEnchantmentOntoInventory(row, enchantmentId, true, enchantmentReaders) as Row;
        if (metadata!.definitionCatalog && fromBaseRecord(row.enchantment_templates))
            for (const name of ['enchantment_template_i', 'enchantment_template_t', 'enchantment_template_t_updated']) delete row.enchantment_templates[name];
        row = compileInventoryFinalEconomy(row, { item_qty: readInventoryField(node, 'item_qty') }, sized.sizeScale,
            { layerValue: readers.layerValue, charges, offHandId: metadata!.offHandId }) as Row;
        row = statuses(row, { prefetched_statuses: { target: { [readInventoryField(node, 'inventory_id') as string]: node.statuses.target },
            caster: { [readInventoryField(node, 'inventory_id') as string]: node.statuses.caster } } }) as Row;
        const environment = type === 'encounter' ? readInventoryFloorEnvironmentContext(possessor!) : {};
        row = compileInventoryEnvironment(row, environment, environmentDependencies) as Row;
        prefetchTemplatePictures(key);
        row = compileInventoryContainer(row, [], [], { ...readers, hasLayer, computedValue: economy,
            queryChildren: () => compileChildren(key, depth + 1) }) as Row;
        row = compileInventoryGrossWeight(row, hasLayer, 'compiled-economy') as Row;
        const links = node.inventory_key_links;
        if (!Array.isArray(links)) throw new FromBaseStageMissing('key-link-input', key);
        row = compileInventoryKeyLinks(row, readInventoryField(node, 'inventory_id'), { ...readers, includeAllLinks: true, lockedId: metadata!.lockedId,
            statuses: target => statusRows(target, 'caster'), fetchLinks: filters => links.filter(link =>
                Object.entries(filters).every(([field, value]) => readers.layerValue(link, field) === value)) }) as Row;
        // The native movement entry gate sees only root layers added by statuses
        // or the environment. Canonical template/provider speed alone does not open it.
        row = compileInventoryBaseSpeeds(row, metadata!.baseSpeed, readers) as Row;
        for (const speed of metadata!.baseSpeed.speedTypes) delete row[speed.variable_name];
        if (!baseTree.encounter_links) throw new FromBaseStageMissing('encounter-link-input', key);
        row = compileInventoryEncounterLinks(row, { exists: value => baseTree.encounter_links!.some(link => readers.layerValue(link, 'encounter_link_from') === value),
            fetchLinks: filters => baseTree.encounter_links!.filter(link => Object.entries(filters).every(([field, value]) => readers.layerValue(link, field) === value))
                .sort((a, b) => phpJsonFloat(readers.layerValue(b, 'encounter_link_i') ?? 0) - phpJsonFloat(readers.layerValue(a, 'encounter_link_i') ?? 0))
                .map(link => compileEncounterLinkRow(link)) }) as Row;
        const states = node.inventory_closure_states;
        if (!Array.isArray(states)) throw new FromBaseStageMissing('closure-state', `source:${key}`);
        const active = states.filter(state => fromBaseRecord(state) && upper(String(field(state, 'inventory_closure_state_closure_id'))) === key
            && Number(field(state, 'inventory_closure_state_deleted') ?? 0) === 0);
        if (active.length > 1) throw new TypeError(`Duplicate closure state: ${key}`);
        const phase = active.length ? field(active[0]!, 'inventory_closure_state_phase') : null;
        if (active.length) closureStates[key] = { phase: ['closed', 'open', 'broken', 'cleared'].includes(String(phase)) ? String(phase) : 'closed',
            revision: Math.trunc(phpJsonFloat(field(active[0]!, 'inventory_closure_state_revision') ?? 0)) };
        row = compileInventoryPictures(row, node.inventory, {}, { ...readers, hasLayer, fetchPictures: pictures, getPicture: value => {
            const hits = pictures({ picture_id: value, picture_deleted: 0 });
            if (hits.length) return hits[0]!;
            if (!baseTree.picture_lookup_ids?.some(id => upper(id) === upper(value)))
                throw new FromBaseStageMissing('pictures', `pointer:${value}`);
            return null;
        } }) as Row;
        // Active status definitions have one authoritative raw catalog source.
        // Collapse only identities vouched by that source; origin pointers stay lazy.
        // The whole catalog's media/PL/choice finalization is still named below.
        if (!metadata!.definitionCatalog && fromBaseRecord(row.statuses)) for (const mode of ['target', 'caster']) {
            const entries = Object.values(readCreatureStatuses(row, mode, definition));
            const normalized = entries.map(entry => {
                if (!fromBaseRecord(entry)) throw new TypeError('Invalid compiled status record');
                const copy = leanStatusEntry(entry) as Row;
                const effect = readers.layerValue(entry, 'status_item_id') ?? readers.layerValue(entry, 'status_arr');
                if (!fromBaseUuid(effect) || !definitions.has(upper(effect)))
                    throw new FromBaseStageMissing('catalog-lean-normalization', 'status-definition-identity');
                const inline = Object.values(readers.layerEntries(entry, 'status_arr'))[0];
                if (!fromBaseRecord(inline) || JSON.stringify(inline) !== JSON.stringify(definition(effect)))
                    throw new FromBaseStageMissing('catalog-lean-normalization', `status-definition-divergence:${effect}`);
                copy.status_item_id = effect; delete copy.status_arr;
                const origin = readers.layerValue(entry, 'status_origin_id');
                if (readers.layerValue(entry, 'status_origin_arr') === origin) delete copy.status_origin_arr;
                const caster = readers.layerValue(entry, 'status_caster_id'), target = readers.layerValue(entry, 'status_target_id');
                if (typeof caster === 'string' && caster !== '' && typeof target === 'string' && upper(caster) === upper(target)) delete copy.status_caster_id;
                return copy;
            });
            if (normalized.length) row.statuses[mode] = normalized; else delete row.statuses[mode];
        }
        // Native surface removal follows the picture bake. Keep the source bucket
        // lean and discard the initializer's temporary legacy mirrors.
        for (const name of ['inventory_id', 'inventory_created_by_id', 'inventory_name', 'inventory_description',
            'inventory_written_in_language', 'inventory_flavor_text', 'inventory_gm_notes', 'inventory_item_id',
            'inventory_cost_gp', 'inventory_size', 'weight_override', 'item_qty', 'equipped', 'enchantment_template_id',
            'inventory_inspection_dc', 'lockpick_degree_start', 'lockpick_degree_end', 'possess_id',
            'inventory_presentation_pic_id', 'inventory_thumbnail_pic_id', 'inventory_unidentified',
            'inventory_deleted', 'inventory_private', 'item_type', 'size', 'cost_gp', 'weight']) delete row[name];
        if (metadata!.definitionCatalog) row = elideEmptyInventoryDefinitionLayers(row);
        built.set(key, row); return fromBaseClone(row);
    }
    try {
        if (definitionIds) {
            const definitions = Object.fromEntries(definitionIds.map(sourceId => {
                const row = metadata.definitionCatalog ? finalizedDefinition(sourceId)
                    : definition(sourceId);
                if (!row) throw new FromBaseInputMissing('definition', sourceId);
                return [upper(sourceId), row];
            }));
            return { kind: 'missing-stage', inventory: [], definitions,
                completedStages: FROM_BASE_INVENTORY_IMPLEMENTED_STAGES.filter(stage => stage.startsWith('definition-')),
                missingStages: metadata.definitionCatalog ? [] : FROM_BASE_INVENTORY_FINALIZED_STAGES };
        }
        prefetchTemplatePictures(owner);
        const inventory = compileChildren(owner, 0);
        // Reference emission and normalization are post-construction projections. Stage-local
        // status/environment inputs stay at their original pre-intern boundary.
        const emittedDefinitions = Object.fromEntries([...prepared].map(([key, row]) => [key, metadata.definitionCatalog
            ? finalizedDefinition(key)! : normalizeInventoryDefinition(constructInventoryDefinitionShells(row, metadata, rawDefinition), metadata.layerCatalog)]));
        return { kind: 'missing-stage', inventory, closureStates, derivedWeights, definitions: emittedDefinitions,
            completedStages: [...FROM_BASE_INVENTORY_IMPLEMENTED_STAGES,
                ...(metadata.definitionCatalog ? FROM_BASE_INVENTORY_FINALIZED_STAGES : []),
                ...(metadata.definitionMedia ? ['definition-audio-references'] : []),
                ...(baseTree.definition_videos ? ['definition-video-presentation'] : []),
                ...(metadata.definitionLayerOrder ? ['definition-layer-order'] : []),
                ...(baseTree.inventory_order ? ['database-collation-order'] : [])],
            missingStages: FROM_BASE_INVENTORY_REMAINING_STAGES.filter(stage =>
                !metadata.definitionCatalog && stage !== 'database-collation-order' && stage !== 'definition-layer-order'
                ||
                stage === 'database-collation-order' && !baseTree.inventory_order
                || stage === 'definition-layer-order' && !metadata.definitionLayerOrder) };
    } catch (error) {
        if (!(error instanceof FromBaseStageMissing || error instanceof FromBaseInputMissing)) throw error;
        const stage = error instanceof FromBaseStageMissing ? error.stage : 'from-base-input-missing';
        return { kind: 'missing-stage', completedStages: [], missingStages: [...new Set([stage, ...FROM_BASE_INVENTORY_REMAINING_STAGES])],
            detail: error instanceof FromBaseStageMissing ? error.detail : `${error.dependency}:${error.id}` };
    }
}
